import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Product, ProductDesignV2, RoutineSet } from './types';
import './set-v2.css';

// The 3-Step Set's own sections on its new-design page (ProductV2 renders the
// set like a product and swaps these in): what's in the box, the scroll story,
// the morning/night switch with the results timeline, the gift box and the
// savings bar. Texts come from the set's design_v2 (Admin > Products >
// 3-Step Set) with the approved draft copy as the default.

const TIME = ['30 sec', '60 sec', '3 min'];
const TEXTURE = ['Water-light', 'Silky', 'Rich'];
const toNumber = (price: string) => Number(price.replace(/[^0-9.]/g, '')) || 0;
const tk = (n: number) => '৳' + n.toLocaleString('en-US');
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function Svg({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {d.split('|').map((p) => (p.startsWith('c:') ? <circle key={p} cx={p.split(',')[1]} cy={p.split(',')[2]} r={p.split(',')[3]} /> : <path key={p} d={p} />))}
    </svg>
  );
}
const TICK = 'M5 12.5l4.5 4.5L19 7.5';
const ARROW = 'M5 12h14M13 6l6 6-6 6';
const BAG = 'M5 8h14l-1 12H6z|M9 8V6a3 3 0 0 1 6 0v2';
const GIFT = 'M3 8h18v13H3z|M3 12h18M12 8v13';

const nameOf = (p: Product) => p.designV2?.name || p.name;
const stepTag = (i: number) => ['01 · OPEN', '02 · TREAT', '03 · LOCK'][i] ?? `0${i + 1}`;

// What's in the box, in the buy column.
export function SetBox({ routine, routineSet, href }: { routine: Product[]; routineSet: RoutineSet | null; href: (p: Product) => string | null }) {
  const single = routine.reduce((sum, p) => sum + toNumber(p.price), 0);
  return (
    <div className="boxl rise" style={{ ['--d' as string]: '.42s' }}>
      <div className="bh">
        <span>
          <Svg d={GIFT} />
          In the gift box
        </span>
        <span>{routine.length} × 50 ml</span>
      </div>
      {routine.map((p, i) => (
        <Link key={p.id} to={href(p) ?? '#'}>
          <img src={p.image} alt="" />
          <span>
            <small>STEP {stepTag(i)}</small>
            <b>{nameOf(p)}</b>
          </span>
          <span className="p">{p.price}</span>
        </Link>
      ))}
      {routineSet && single > 0 && (
        <div className="tot">
          <span>One by one {tk(single)}</span>
          <span>
            In the set <b>{routineSet.price}</b>
          </span>
        </div>
      )}
    </div>
  );
}

// The photo stays while the three steps scroll past.
export function SetStory({ routine, v, href }: { routine: Product[]; v: ProductDesignV2; href: (p: Product) => string | null }) {
  const root = useRef<HTMLElement>(null);
  const [step, setStep] = useState(0);
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && setStep(Number((e.target as HTMLElement).dataset.i))), { rootMargin: '-45% 0px -45% 0px' });
    el.querySelectorAll('.chap').forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [routine.length]);
  const s = v.story ?? {};
  const img = (p: Product) => p.designV2?.ritual?.image || p.image;
  return (
    <section className="sec story" ref={root} data-step={step}>
      <div className="wrap">
        <div className="stick">
          <div className="frame">
            {routine.map((p, i) => (
              <img key={p.id} src={img(p)} alt={nameOf(p)} className={i === step ? 'on' : ''} loading="lazy" />
            ))}
            <span className="big">0{step + 1}</span>
          </div>
          <div className="prog">
            {routine.map((p, i) => (
              <i key={p.id} className={i === step ? 'on' : ''} />
            ))}
          </div>
        </div>
        <div className="chaps">
          <div className="head rv" style={{ margin: '0 0 10px' }}>
            <span className="kicker">{s.kicker || 'The five-minute ritual'}</span>
            <h2>
              {s.title || 'Three steps,'}
              <br />
              <em>{s.titleEm || 'one routine.'}</em>
            </h2>
            <p>{s.lead || 'Each step makes the next one work better. Scroll through them in the order you use them.'}</p>
          </div>
          {routine.map((p, i) => {
            const ings = (p.designV2?.ingredients?.items?.map((x) => x.name) ?? p.keyIngredients.map((k) => k.name)).slice(0, 4);
            return (
              <article key={p.id} className={`chap${i === step ? ' on' : ''}`} data-i={i}>
                <figure className="mimg">
                  <img src={img(p)} alt={nameOf(p)} loading="lazy" />
                  <span>0{i + 1}</span>
                </figure>
                <span className="badge">
                  <i />
                  STEP {stepTag(i)}
                </span>
                <h3>{nameOf(p)}</h3>
                <p className="sl">{p.subtitle}</p>
                <p className="tx">{s.steps?.[i]?.text || p.designV2?.homeLine || p.description}</p>
                <div className="meta">
                  <span>
                    <Svg d="c:,12,12,9|M12 7v5l3 2" />
                    {TIME[i]}
                  </span>
                  <span>{TEXTURE[i]} texture</span>
                  {ings.map((x) => (
                    <span key={x} className="k">
                      {x}
                    </span>
                  ))}
                </div>
                <div className="foot">
                  <b>{p.price}</b>
                  <Link className="lnk" to={href(p) ?? '#'}>
                    See step 0{i + 1} <Svg d={ARROW} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const RESULTS = [
  { period: '3 days', title: 'Softer, more hydrated', text: 'Skin feels comfortable, without the tight feeling after washing.' },
  { period: '2 weeks', title: 'Smoother, more even', text: 'Texture looks refined and pores less visible as oil comes into balance.' },
  { period: '4–6 weeks', title: 'Marks fade, glow shows', text: 'Dark spots and post-acne marks look lighter; the barrier feels stronger.' },
];

// Morning / night switch with the routine flow, then the results timeline.
export function SetDayNight({ routine, v }: { routine: Product[]; v: ProductDesignV2 }) {
  const [night, setNight] = useState(false);
  const results = v.results?.length ? v.results : RESULTS;
  const short = (p: Product, i: number) => p.designV2?.short || ['Booster', 'Serum', 'Cream'][i];
  const how = [`2–3 pumps, pressed in`, '1–2 pumps', night ? 'Repairs overnight' : 'Locks in moisture'];
  return (
    <section className={`sec dn${night ? ' night' : ''}`}>
      <div className="wrap">
        <div className="head center rv" style={{ marginBottom: 0 }}>
          <span className="kicker">Morning and night</span>
          <h2>
            The same three steps,
            <br />
            <em>twice a day.</em>
          </h2>
          <div className="sw" role="group" aria-label="Morning or night">
            <button type="button" aria-pressed={!night} onClick={() => setNight(false)}>
              <Svg d="c:,12,12,4|M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
              Morning
            </button>
            <button type="button" aria-pressed={night} onClick={() => setNight(true)}>
              <Svg d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
              Night
            </button>
          </div>
        </div>
        <ol className="flow rv">
          <li>
            <small>CLEANSE</small>
            <b>{night ? 'Oil cleanser, then foam' : 'Gentle cleanser'}</b>
            <span>Leave skin a little damp</span>
          </li>
          {routine.map((p, i) => (
            <li key={p.id} className="k">
              <small>
                STEP 0{i + 1} · {TIME[i].toUpperCase()}
              </small>
              <b>{short(p, i)}</b>
              <span>{how[i]}</span>
            </li>
          ))}
          <li className={night ? 'off' : ''}>
            <small>LAST</small>
            <b>Sunscreen</b>
            <span>Every morning</span>
          </li>
        </ol>
        <div className="rtl2 rv">
          {results.map((r, i) => (
            <div key={i}>
              <i>{['3D', '2W', '6W'][i] ?? i + 1}</i>
              <small>{r.period.toUpperCase()}</small>
              <b>{r.title}</b>
              <p>{r.text}</p>
            </div>
          ))}
        </div>
        <p className="rnote rv">{v.resultsNote || 'With daily use, morning and night, and sunscreen. Individual results vary.'}</p>
      </div>
    </section>
  );
}

// The gift box: its lid lifts away as the box scrolls into view.
export function SetGift({ v, price, onAdd }: { v: ProductDesignV2; price: string; onAdd: () => void }) {
  const stage = useRef<HTMLDivElement>(null);
  const g = v.gift ?? {};
  useEffect(() => {
    const s = stage.current;
    if (!s) return;
    if (reduced()) {
      s.style.setProperty('--p', '1');
      return;
    }
    let raf = 0;
    const f = () => {
      raf = 0;
      const r = s.getBoundingClientRect();
      const vh = window.innerHeight;
      s.style.setProperty('--p', Math.max(0, Math.min(1, (vh * 0.85 - r.top) / (vh * 0.6))).toFixed(3));
    };
    const on = () => {
      if (!raf) raf = requestAnimationFrame(f);
    };
    window.addEventListener('scroll', on, { passive: true });
    f();
    return () => {
      window.removeEventListener('scroll', on);
      cancelAnimationFrame(raf);
    };
  }, []);
  const occasions = g.occasions?.length ? g.occasions : ['Eid', 'Birthday', 'Anniversary', 'Wedding', 'Just because'];
  return (
    <section className="sec soft unbox">
      <div className="wrap">
        <div className="stage2" ref={stage}>
          <img src={g.image || '/v2/giftbox.webp'} alt="The 3-Step Barrier Set gift box, open" loading="lazy" />
          <div className="lid" aria-hidden="true">
            <span className="seal">
              <span>
                <b>
                  Rx<span>.</span>K SEOUL
                </b>
                <small>3-STEP RITUAL</small>
              </span>
            </span>
          </div>
          <span className="hint" aria-hidden="true">
            SCROLL TO OPEN
          </span>
        </div>
        <div className="head rv" style={{ margin: 0 }}>
          <span className="kicker">{g.kicker || 'The gift box'}</span>
          <h2>
            {g.title || 'Made to'}
            <br />
            <em>{g.titleEm || 'be given.'}</em>
          </h2>
          <p>{g.text || 'All three steps arrive in the signature Rx.K Seoul box, with a step-by-step card inside. No wrapping needed.'}</p>
          <div className="occ">
            {occasions.map((o) => (
              <span key={o}>{o}</span>
            ))}
          </div>
          <ul className="gnote">
            {['Gift box included at no extra cost', 'Free delivery, anywhere in Bangladesh', 'Pay the rider when it arrives'].map((x) => (
              <li key={x}>
                <Svg d={TICK} />
                <span>{x}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="btn wine" onClick={onAdd}>
            <span className="ic">
              <Svg d={BAG} />
            </span>
            Send it as a gift · {price}
          </button>
        </div>
      </div>
    </section>
  );
}

// One by one vs the set: the set price counts down when the bar shows.
export function SetSavings({ routine, routineSet, onBuy }: { routine: Product[]; routineSet: RoutineSet; onBuy: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const single = routine.reduce((sum, p) => sum + toNumber(p.price), 0);
  const setPrice = toNumber(routineSet.price);
  const [shown, setShown] = useState(single);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let raf = 0;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        setOn(true);
        if (reduced()) return setShown(setPrice);
        const t0 = performance.now() + 300;
        const f = (t: number) => {
          const p = Math.max(0, Math.min(1, (t - t0) / 1600));
          setShown(single - (single - setPrice) * (1 - Math.pow(1 - p, 3)));
          if (p < 1) raf = requestAnimationFrame(f);
        };
        raf = requestAnimationFrame(f);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [single, setPrice]);
  if (!single || setPrice >= single) return null;
  const pct = (setPrice / single) * 100;
  return (
    <div className={`save3 rv${on ? ' in' : ''}`} ref={box}>
      <div className="nums">
        <div>
          <small>ONE BY ONE</small>
          <span className="was">{tk(single)}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <small>THE 3-STEP SET</small>
          <span className="now">{tk(Math.round(shown))}</span>
        </div>
      </div>
      <div className="bar">
        <i style={on ? { width: `${pct}%` } : undefined} />
        <b style={on ? { width: `${100 - pct}%` } : undefined} />
      </div>
      <div className="lg">
        <span>{routine.map((p, i) => `${p.designV2?.short || ['Booster', 'Serum', 'Cream'][i]} ${p.price}`).join(' + ')}</span>
        <span>
          You save <b>{tk(single - setPrice)}</b>
        </span>
      </div>
      <ul>
        {['All three steps', 'Gift box included', 'Free delivery'].map((x) => (
          <li key={x}>
            <Svg d={TICK} />
            {x}
          </li>
        ))}
        <button type="button" className="btn wine" onClick={onBuy}>
          Buy the set · COD
        </button>
      </ul>
    </div>
  );
}
